import React, { Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import AddressItem from './AddressItem';
import SummaryItem from './SummaryItem';

// Utils

import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { STATE_NAMES } from '@constants/configs/states';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';


const AddressSummaryOfPerson = props => {

    const {
        addressVisibilityDueToGeneralDelivery,
        ButtonContainer,
        otherAddresses,
        householdMembers,
        isState,
        itfFeatureEnabled,
        livingArrangementEnabled,
        stateCode
    } = props;


    const primaryContactPersonIndex = useMemo(() => {
        return householdMemberTools.getPrimaryContactPersonIndex(householdMembers);
    }, [householdMembers]);

    return householdMembers.map((member, i) => {
        const {
            addressId,
            applicantGuid,
            name,
            livingArrangement,
            householdContact: {
                homeAddress,
                mailingAddress,
                mailingAddressSameAsHomeAddressIndicator,
            },
            livesAtOtherAddressIndicator,
            noHomeAddressIndicator,
            otherAddress: {
                livingOutsideofStateTemporarilyIndicator
            }
        } = member;


        const getHomeAddress = () => {
            const mixin = addressVisibilityDueToGeneralDelivery[applicantGuid]
                ? { isStreetAddress1Hidden: true }
                : { };

            return i === primaryContactPersonIndex ?
                <AddressItem
                    textCode = 'common.home_address'
                    { ...homeAddress }
                    { ...mixin }
                /> :
                <AddressItem
                    textCode = {
                        livesAtOtherAddressIndicator
                            ? 'common.other_address'
                            : 'common.home_address'
                    }
                    { ...(otherAddresses.find(a => a.addressId === addressId)) }
                    { ...mixin }
                />;
        };

        const getnoHomeAddressIndicator = () => {
            return(
                i === primaryContactPersonIndex ?
                    <div className = 'grid-row gi-summary__address' >
                        <SummaryItem
                            textCode = 'common.no_home_address'                        
                            value = { !!noHomeAddressIndicator }
                            addressItemClasses
                        />
                    </div>
                    :
                    null
            );
        };


        const getMailingAddress = () => {
            switch(i) {
                case primaryContactPersonIndex:
                    return mailingAddressSameAsHomeAddressIndicator ?
                        <div className = 'grid-row' >
                            <SummaryItem
                                category = 'common.mailing_address'
                                textCode = ''
                                value = 'common.same_as_home'
                                addressItemClasses
                            />
                        </div> :
                        <AddressItem
                            textCode = 'common.mailing_address'
                            { ...mailingAddress }
                        />;
                default:
                    if(itfFeatureEnabled) {
                        return (
                            <AddressItem
                                textCode = 'common.mailing_address'
                                { ...mailingAddress }
                            />
                        );
                    } else {
                        return null;
                    }
            }
        };

        const getLivingArrangement = (livingArrangement) => {
            return DROPDOWN_OPTIONS.LIVING_ARRANGEMENT.find(type => type.value === livingArrangement)?.text ?? '';
        };

        return (
            <div
                className = 'summary__block margin-b-20'
                id = 'addressSummary'
                key = { `${ member.name.firstName }${ i }` }
            >
                <div className = 'summary__header'>
                    <div className = 'grid-row'>
                        <h4 className = 'mobile:grid-col-12 tablet:grid-col-fill'>
                            <Fragment>
                                { normalizeNameOfPerson(name) }
                                {
                                    i === primaryContactPersonIndex
                                        ? <small>&nbsp;<FormattedMessage id = 'common.primary_contact' /></small>
                                        : null
                                }
                            </Fragment>
                        </h4>
                        <div className = 'mobile:grid-col-12 tablet:grid-col-auto float-right'>
                            {
                                <ButtonContainer
                                    customButtonClassName = 'btn-small'
                                    type = 'editButton_secondary'
                                    args = { ['editAddress', i] }
                                    ariaLabel = {`Edit address for ${member.name.firstName} ${member.name.lastName}` }
                                />
                            }
                        </div>
                    </div>
                </div>
                <div className = 'summary_body'>
                    { getHomeAddress() }
                    {
                        livingArrangementEnabled && <SummaryItem
                            textCode='ssap.input.label.living_arrangement_summary_question'
                            value={getLivingArrangement(livingArrangement)}
                            summaryItemZeroPaddingClasses
                            desc={[
                                [
                                    `taxHousehold.householdMember[${i}].livingArrangement`,
                                    getLivingArrangement
                                ]
                            ]}
                            summaryItemId={'livingArrangement'}
                        />
                    }
                    {
                        livingOutsideofStateTemporarilyIndicator !== null
                            ? (
                                <SummaryItem
                                    replacements = { { exchangeState: STATE_NAMES[stateCode] } }
                                    summaryItemZeroPaddingClasses
                                    textCode = 'common.are_you_living_outside_state'
                                    value = { livingOutsideofStateTemporarilyIndicator }
                                />
                            )
                            : null
                    }
                    {
                        ( isState.GA || isState.VA ) &&
                        getnoHomeAddressIndicator()
                    }
                    { getMailingAddress() }
                </div>
            </div>
        );
    });
};

export default AddressSummaryOfPerson;
